class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    /** Set when /search/suggest returns zero results with a category selected; cleared on submit or when results return. */
    this.pendingCategoryResetOnSubmit = false;
    /** Skip suggest refetch when programmatically clearing category right before form submit. */
    this._skipCategoryPredictiveRefetch = false;
    this.setupEventListeners();
  }

  /** @type {Record<string, Promise<Set<string>>>} */
  static collectionHandlesPromises = Object.create(null);

  /**
   * Cache key: collection handle + normalized suggest query string (the exact `q`
   * sent to /search/suggest, which may include product_type:... AND user terms).
   */
  getSuggestCacheKey(suggestQuery, collectionHandle) {
    const prefix = collectionHandle ? `${collectionHandle}|` : '';
    return prefix + suggestQuery.replace(/\s+/g, '-').toLowerCase();
  }

  getCollectionHandleFromForm() {
    const categorySelect = this.input?.form?.querySelector('.header-search__category');
    if (!categorySelect || categorySelect.selectedIndex <= 0) return null;
    const val = (categorySelect.value || '').trim();
    if (!val) return null;
    if (!val.includes('/')) {
      return val;
    }
    try {
      const u = new URL(val, window.location.origin);
      const m = u.pathname.match(/\/collections\/([^/]+)(?:\/search)?$/);
      return m ? m[1] : null;
    } catch {
      return null;
    }
  }

  /** @type {Record<string, Promise<string | null>>} */
  static collectionSampleProductTypePromises = Object.create(null);

  /**
   * First product's product_type in the collection (limit=1) — used to build
   * Storefront search syntax: product_type:Hoodies AND user terms (matches Shopify suggest).
   */
  getSampleProductTypeForCollection(collectionHandle) {
    if (PredictiveSearch.collectionSampleProductTypePromises[collectionHandle]) {
      return PredictiveSearch.collectionSampleProductTypePromises[collectionHandle];
    }

    const promise = (async () => {
      const url = `/collections/${encodeURIComponent(collectionHandle)}/products.json?limit=1`;
      const res = await fetch(url);
      if (!res.ok) return null;
      const data = await res.json();
      const products = data.products || [];
      const p0 = products[0];
      if (!p0) return null;
      const pt = p0.product_type;
      return typeof pt === 'string' && pt.trim() ? pt.trim() : null;
    })();

    PredictiveSearch.collectionSampleProductTypePromises[collectionHandle] = promise;
    return promise;
  }

  formatProductTypeForSuggestQuery(productType) {
    const t = (productType || '').trim();
    if (!t) return '';
    if (/[\s"]/.test(t)) {
      return `"${t.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
    }
    return t;
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (this.pendingCategoryResetOnSubmit) {
      const categorySelect = this.input?.form?.querySelector('.header-search__category');
      if (categorySelect && categorySelect.selectedIndex > 0) {
        event.preventDefault();
        this.pendingCategoryResetOnSubmit = false;
        this.allPredictiveSearchInstances.forEach((inst) => {
          inst.pendingCategoryResetOnSubmit = false;
        });
        this.clearCategorySelectionForSubmit();
        // Use native submit() so navigation happens in one gesture. requestSubmit()
        // re-dispatches the submit event and can require a second click to reach /search.
        this.input.form.submit();
        return;
      }
    }
    this.pendingCategoryResetOnSubmit = false;
    this.allPredictiveSearchInstances.forEach((inst) => {
      inst.pendingCategoryResetOnSubmit = false;
    });

    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const routesRoot = typeof window !== 'undefined' && window.routes ? window.routes : typeof routes !== 'undefined' ? routes : {};
    const basePath = routesRoot.predictive_search_url || '/search/suggest';
    const sectionId = routesRoot.predictive_search_section_id || 'predictive-search';
    const categorySelect = this.input?.form?.querySelector('.header-search__category');
    const scopedToCollection = Boolean(categorySelect && categorySelect.selectedIndex > 0);
    const collectionHandle = scopedToCollection ? this.getCollectionHandleFromForm() : null;

    this.setLiveRegionLoadingState();

    const run = async () => {
      let suggestQuery = searchTerm;
      if (scopedToCollection && collectionHandle) {
        const productType = await this.getSampleProductTypeForCollection(collectionHandle);
        if (productType) {
          const safeType = this.formatProductTypeForSuggestQuery(productType);
          suggestQuery = `product_type:${safeType} AND ${searchTerm}`;
        }
      }

      const queryKey = this.getSuggestCacheKey(suggestQuery, collectionHandle);

      if (this.cachedResults[queryKey]) {
        this.renderSearchResults(this.cachedResults[queryKey]);
        return;
      }

      const suggestUrl = new URL(basePath, window.location.origin);
      suggestUrl.searchParams.set('q', suggestQuery);
      suggestUrl.searchParams.set('section_id', sectionId);
      suggestUrl.searchParams.set('resources[limit]', '10');
      suggestUrl.searchParams.set('resources[limit_scope]', 'each');
      if (scopedToCollection) {
        suggestUrl.searchParams.set('resources[type]', 'product,query');
      }

      const response = await fetch(suggestUrl.toString(), {
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        const error = new Error(response.status);
        this.close();
        throw error;
      }

      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const sectionEl = doc.getElementById(`shopify-section-${sectionId}`);
      if (!sectionEl) {
        this.close();
        throw new Error('predictive-search section markup missing');
      }
      const resultsMarkup = sectionEl.innerHTML;

      this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
        predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
      });
      this.renderSearchResults(resultsMarkup);
    };

    run().catch((error) => {
      if (error?.code === 20) {
        return;
      }
      this.close();
      throw error;
    });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  /**
   * Clears category on all search forms without refetching suggest (used right before navigating via submit).
   */
  clearCategorySelectionForSubmit() {
    this.allPredictiveSearchInstances.forEach((inst) => {
      inst._skipCategoryPredictiveRefetch = true;
    });
    document.querySelectorAll('.header-search__category').forEach((select) => {
      if (select.selectedIndex > 0) {
        select.selectedIndex = 0;
      }
    });
    const first = document.querySelector('.header-search__category');
    if (first) {
      first.dispatchEvent(new Event('change', { bubbles: true }));
    }
    this.allPredictiveSearchInstances.forEach((inst) => {
      inst._skipCategoryPredictiveRefetch = false;
    });
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    const categorySelect = this.input?.form?.querySelector('.header-search__category');
    const scoped = Boolean(categorySelect && categorySelect.selectedIndex > 0);
    const root = this.predictiveSearchResults.querySelector('#predictive-search-results');
    const noResultsFromApi = root?.getAttribute('data-predictive-search-no-results') === 'true';

    if (scoped && noResultsFromApi) {
      this.allPredictiveSearchInstances.forEach((inst) => {
        inst.pendingCategoryResetOnSubmit = true;
      });
    } else {
      this.allPredictiveSearchInstances.forEach((inst) => {
        inst.pendingCategoryResetOnSubmit = false;
      });
    }

    const finish = () => {
      this.setLiveRegionResults();
      this.open();
    };

    if (scoped) {
      this.filterPredictiveProductsToCollection().then(finish).catch(finish);
    } else {
      finish();
    }
  }

  /**
   * Call when the category dropdown changes so cached suggest results are not reused
   * across different collections for the same search term.
   */
  onCategoryChanged() {
    if (this._skipCategoryPredictiveRefetch) {
      return;
    }
    this.allPredictiveSearchInstances.forEach((inst) => {
      inst.cachedResults = {};
      inst.pendingCategoryResetOnSubmit = false;
    });
    this.abortController.abort();
    this.abortController = new AbortController();
    const q = this.getQuery();
    if (q.length) {
      this.getSearchResults(q);
    }
  }

  /**
   * Fetches product handles for a collection (paginated) so we can drop suggest
   * products that are not in the selected category. Cached per collection handle.
   */
  getCollectionProductHandlesSet(collectionHandle) {
    if (PredictiveSearch.collectionHandlesPromises[collectionHandle]) {
      return PredictiveSearch.collectionHandlesPromises[collectionHandle];
    }

    const promise = (async () => {
      const handles = new Set();
      let page = 1;
      const maxPages = 20;
      while (page <= maxPages) {
        const url = `/collections/${encodeURIComponent(collectionHandle)}/products.json?limit=250&page=${page}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(String(res.status));
        const data = await res.json();
        const products = data.products || [];
        products.forEach((p) => {
          if (p && p.handle) handles.add(p.handle);
        });
        if (products.length < 250) break;
        page += 1;
      }
      return handles;
    })();

    PredictiveSearch.collectionHandlesPromises[collectionHandle] = promise;
    return promise;
  }

  async filterPredictiveProductsToCollection() {
    const form = this.input?.form;
    if (!form || !this.predictiveSearchResults) return;

    const categorySelect = form.querySelector('.header-search__category');
    if (!categorySelect || categorySelect.selectedIndex <= 0) return;

    const collectionHandle = this.getCollectionHandleFromForm();
    if (!collectionHandle) return;

    let handles;
    try {
      handles = await this.getCollectionProductHandlesSet(collectionHandle);
    } catch {
      return;
    }

    const list = this.predictiveSearchResults.querySelector('#predictive-search-results-products-list');
    if (!list) return;

    list.querySelectorAll(':scope > li').forEach((li) => {
      const a = li.querySelector('a[href*="/products/"]');
      if (!a) return;
      const href = a.getAttribute('href') || '';
      const m = href.match(/\/products\/([^/?]+)/);
      if (!m) return;
      if (!handles.has(m[1])) {
        li.remove();
      }
    });

    if (!list.querySelector('li')) {
      const wrap = document.getElementById('predictive-search-products');
      if (wrap) {
        const container = wrap.closest('div');
        container?.remove();
      }
    }
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const countEl = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (countEl) {
      this.setLiveRegionText(countEl.textContent);
    }
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
